import {Autocomplete, Box, Loader, Title} from '@mantine/core';
import Head from 'next/head';
import {useRouter} from 'next/router';
import {parseCookies} from 'nookies';
import type React from 'react';
import {useEffect, useState} from 'react';
import {AiOutlineClose, AiOutlineSearch} from 'react-icons/ai';
import {HiOfficeBuilding} from 'react-icons/hi';
import {
  MdExpandLess,
  MdExpandMore,
  MdOutlineExplore,
  MdSearch,
} from 'react-icons/md';
import {
  COLOR_CATEGORY_2,
  COLOR_CATEGORY_5,
  COLOR_CATEGORY_10,
} from '../../../config/constants/colorCodes';
import {GoogleAdBannerName} from '../../../config/constants/googleAdBanner';
import {GTAG_LOCATION} from '../../../config/constants/gtag';
import {GSSKT_HOST} from '../../../config/constants/hosts';
import type {SearchType} from '../../../core/entities/search/constants';
import type {SearchKeywordItemResponse} from '../../../core/entities/search/schema';
import {validateCookie} from '../../../core/usecases/auth/checkOauthTokenExistence';
import {GoogleAdBanner} from '../../../shared/components/Banner/GoogleAdBanner';
import {SpHeaderBanner} from '../../../shared/components/Banner/SpHeaderBanner';
import {CommonLink} from '../../../shared/components/CommonLink';
import {GeneralFooter} from '../../../shared/components/Footer/general';
import {GeneralHeaderContainer} from '../../../shared/components/Header/General/container';
import {SignUp} from '../../../shared/components/Sidebar/SignUp';
import {useIsAppWebView} from '../../../shared/hooks/useIsAppWebView';
import {useGoogleAdBanner} from '../../../shared/services/useGoogleAdBanner';
import {useMediaQuery} from '../../../shared/services/useMediaQuery';
import {useSearchKeyword} from '../usecase/useSearchKeyword';

type Props = {
  companyNameList?: string[];
};

const getItemByType = (type: string): string => {
  switch (type) {
    case 'recruiting_info': {
      return '募集情報';
    }

    case 'company_page': {
      return '企業情報';
    }

    case 'columns': {
      return 'コラム';
    }

    default: {
      return '';
    }
  }
};

const getColorByType = (type: string): string => {
  switch (type) {
    case 'recruiting_info': {
      return COLOR_CATEGORY_5;
    }

    case 'company_page': {
      return COLOR_CATEGORY_10;
    }

    case 'columns': {
      return COLOR_CATEGORY_2;
    }

    default: {
      return '';
    }
  }
};

export const Page: React.FC<Props> = ({companyNameList}) => {
  const isWebView = useIsAppWebView();
  const cookies = parseCookies();
  const {isValid} = validateCookie(cookies);

  const router = useRouter();
  const {q} = router.query;
  const {type: t} = router.query;
  const currentUrl = `${GSSKT_HOST}${decodeURI(router.pathname)}`;

  const [searchWord, setSearchWord] = useState<string>(
    typeof q === 'string' ? q : '',
  );
  const [searchType, setSearchType] = useState<string>(
    typeof t === 'string' ? t : '',
  );
  const {
    getSearchKeyword,
    getSearchKeywordNextPage,
    isLoading,
    searchResult,
    nextPage,
    resultCount,
    recommendKeywords,
    type,
  } = useSearchKeyword(searchWord, searchType);
  const {isPC} = useMediaQuery();
  const [isExpanded, setIsExpanded] = useState(false);
  const keywords = searchWord.split(/[\s　]+/);

  // DFPバナーのセット
  const googleAdBannerNames = [
    GoogleAdBannerName.B_RECTANGLE,
    GoogleAdBannerName.B_RECTANGLE_BOTTOM,
    GoogleAdBannerName.D_FEED_LEFT_SMALL,
  ];
  useGoogleAdBanner(googleAdBannerNames);

  // URL変更を検知して検索フォームを更新、検索結果を取得する
  useEffect(() => {
    router.events.on('routeChangeComplete', handleRouteChange);
    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
    };
  }, [router.events]);

  const handleRouteChange = (url: string) => {
    const newUrl = new URL(`${GSSKT_HOST}${url}`);
    const newSearchWord = newUrl.searchParams.get('q') || '';
    const newSearchType = newUrl.searchParams.get('type') || '';
    const decodedSearchWord = decodeURIComponent(newSearchWord);
    setSearchWord(decodedSearchWord);
    setSearchType(newSearchType);
    void getSearchKeyword(decodedSearchWord, newSearchType, 1);
  };

  const handleSearchSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const encodedSearchWord = encodeURIComponent(searchWord);
    void router.push(`/search?q=${encodedSearchWord}`);
  };

  const handleFilterClick = (type: SearchType) => {
    const encodedSearchWord = encodeURIComponent(searchWord);
    void router.push(`/search?q=${encodedSearchWord}&type=${type}`);
  };

  const toggleButton = () => {
    setIsExpanded(!isExpanded);
  };

  /**
   * キーワードを中心とする指定文字数で枠組みを作成する
   * @param text 検索結果一覧の表示コンテンツのテキスト
   * @param keyword 対象のキーワード
   * @param maxDescriptionLength 検索結果一覧の表示コンテンツ最大文字数
   * @returns
   */
  const excerpt = (
    text: string,
    keyword: string,
    maxDescriptionLength: number,
  ) => {
    const keywordIndex = text.indexOf(keyword);
    if (keywordIndex === -1) {
      return text.slice(
        0,
        Math.max(0, Math.min(maxDescriptionLength, text.length)),
      );
    }

    const start = Math.max(0, keywordIndex - maxDescriptionLength);
    const end = Math.min(
      text.length,
      keywordIndex + keyword.length + maxDescriptionLength,
    );
    return text.slice(start, end);
  };

  const truncateContent = (item: SearchKeywordItemResponse) => {
    if (item.type === 'columns') {
      return `${item.content.slice(0, 100)}...`;
    }

    if (item.type === 'recruiting_info') {
      return `${item.content.slice(0, 50)}...`;
    }

    return excerpt(item.content, keywords[0]!, 70);
  };

  return (
    <>
      <Head>
        <title key="title">「{searchWord}」就活情報 | 外資就活ドットコム</title>
        <meta key="og:title" property="og:title" content="検索結果" />
        <link rel="canonical" href={currentUrl} key="canonical" />
      </Head>
      {!isWebView && <GeneralHeaderContainer />}
      {isValid && (
        <SpHeaderBanner
          location={GTAG_LOCATION.SEARCH}
          className="w-full pc:hidden"
        />
      )}
      <main>
        <Box className="py-12 pc:bg-background-1 pc:py-32 relative h-[100%]">
          <Box className="mx-auto my-0 max-w-[1200px]">
            <Box className="flex justify-between">
              {isPC && (
                // 左カラム
                <Box className="w-[200px] text-base sticky top-[150px] flex flex-col gap-y-8">
                  <Box className="sticky top-[150px] flex flex-col">
                    <div className="bg-primary-1 px-12 py-8 font-bold text-white-1">
                      検索結果を絞り込む
                    </div>
                    <div className="bg-white px-20 py-8">
                      <div className="sticky top-[150px]">
                        <label
                          className="block"
                          onClick={() => {
                            handleFilterClick('recruiting_info');
                          }}
                        >
                          <input
                            type="radio"
                            name="type"
                            checked={type === 'recruiting_info'}
                            className="mr-8"
                          />
                          <span>募集情報</span>
                        </label>
                        <label
                          className="block"
                          onClick={() => {
                            handleFilterClick('company_page');
                          }}
                        >
                          <input
                            type="radio"
                            name="type"
                            checked={type === 'company_page'}
                            className="mr-8"
                          />
                          <span>企業情報</span>
                        </label>
                        <label
                          className="block"
                          onClick={() => {
                            handleFilterClick('columns');
                          }}
                        >
                          <input
                            type="radio"
                            name="type"
                            checked={type === 'columns'}
                            className="mr-8"
                          />
                          <span>コラム</span>
                        </label>
                        <button
                          className="text-primary hover:cursor-pointer"
                          onClick={() => {
                            handleFilterClick('');
                          }}
                        >
                          ☓ 解除
                        </button>
                      </div>
                    </div>
                    <div className="py-8">
                      <GoogleAdBanner
                        googleAdBannerName={
                          GoogleAdBannerName.D_FEED_LEFT_SMALL
                        }
                      />
                    </div>
                  </Box>
                </Box>
              )}
              {/* 中央カラム */}
              <Box className="w-[600px]">
                {isPC && (
                  <Box className="mb-8 w-full max-w-[664px]">
                    <Title
                      order={1}
                      className="border-t-4 border-primary-1 bg-white p-16 text-lg text-primary-1 pc:px-32"
                    >
                      <div className="flex">
                        {isPC && (
                          <div>
                            検索結果: {searchWord} {resultCount}件{' '}
                          </div>
                        )}
                      </div>
                    </Title>
                    <div className="m-8">
                      <form onSubmit={handleSearchSubmit}>
                        <Autocomplete
                          icon={<MdSearch />}
                          value={searchWord}
                          dropdownPosition="bottom"
                          limit={1000}
                          maxDropdownHeight={320}
                          className="w-100"
                          placeholder="サイト内検索"
                          data={companyNameList ?? []}
                          onChange={(e) => {
                            setSearchWord(e);
                          }}
                          itemComponent={(props) => (
                            <CommonLink
                              key={`search-company-${props.id}`}
                              href={`/search?q=${props.value}`}
                              className="border-solid border-b-2 border-line last:border-b-0 cursor-pointer flex items-center line-clamp-2 hover:underline text-primary p-4"
                            >
                              {props.value}
                            </CommonLink>
                          )}
                        />
                      </form>
                    </div>
                  </Box>
                )}
                {!isPC && (
                  <Box className="mb-8 w-full max-w-[664px]">
                    <Title
                      order={1}
                      className="flex items-center justify-between border-t-4 border-primary-1 bg-white p-16 text-lg text-primary-1 pc:px-32"
                    >
                      <div className="flex items-center">
                        <AiOutlineSearch size="30" fontSize={20} />
                        <div className="text-black">
                          検索結果 {resultCount}件
                        </div>
                      </div>
                      <button
                        className="ml-auto flex items-center justify-center rounded border border-primary-1 px-12 py-8 text-md"
                        onClick={toggleButton}
                      >
                        {isExpanded ? (
                          <>
                            <MdExpandLess size="30" />
                            閉じる
                          </>
                        ) : (
                          <>
                            <MdExpandMore size="30" />
                            絞り込む
                          </>
                        )}
                      </button>
                    </Title>
                    {isExpanded && (
                      <div className="flex items-center bg-white">
                        <button
                          className={`p-16 ${
                            type === 'company_page' ? 'bg-black-2' : ''
                          }`}
                          onClick={() => {
                            handleFilterClick('company_page');
                          }}
                        >
                          企業
                        </button>
                        <button
                          className={`p-16 ${
                            type === 'recruiting_info' ? 'bg-black-2' : ''
                          }`}
                          onClick={() => {
                            handleFilterClick('recruiting_info');
                          }}
                        >
                          募集
                        </button>
                        <button
                          className={`p-16 ${
                            type === 'columns' ? 'bg-black-2' : ''
                          }`}
                          onClick={() => {
                            handleFilterClick('columns');
                          }}
                        >
                          コラム
                        </button>
                        {type !== '' && (
                          <button
                            className="mx-8 px-16 py-8 text-sm"
                            onClick={() => {
                              handleFilterClick('');
                            }}
                          >
                            <AiOutlineClose size={20} />
                            解除
                          </button>
                        )}
                      </div>
                    )}
                  </Box>
                )}
                {isLoading ? (
                  <Box className="flex justify-center items-center w-full my-40">
                    <Loader color="gray" />
                  </Box>
                ) : (
                  <>
                    <div className="bg-white p-32">
                      <ul>
                        {searchResult.length > 0 ? (
                          searchResult.map(
                            (
                              item: SearchKeywordItemResponse,
                              index: number,
                            ) => {
                              return (
                                <li
                                  key={item.title}
                                  className={`${
                                    index === 0
                                      ? ''
                                      : 'border-solid border-t-2 border-line'
                                  } py-16`}
                                >
                                  <CommonLink href={item.url} className="">
                                    <div className="flex">
                                      <div className="min-w-[44px] py-16 text-center">
                                        <div className="text-xs">
                                          {item.type === 'company_page' ? (
                                            <HiOfficeBuilding
                                              size="30"
                                              className="mx-auto"
                                            />
                                          ) : (
                                            <MdOutlineExplore
                                              size="30"
                                              color={getColorByType(item.type)}
                                              className="mx-auto"
                                            />
                                          )}
                                          {getItemByType(item.type)}
                                        </div>
                                      </div>
                                      <div className="sm:text-sm overflow-hidden text-ellipsis py-16 font-bold">
                                        {item.title.length > 30
                                          ? `${item.title.slice(0, 30)}…`
                                          : item.title}
                                      </div>
                                    </div>
                                    <div className="text-sm text-black-1">
                                      {truncateContent(item)}
                                      {item.postDate.length > 0 && (
                                        <div>{item.postDate} 更新</div>
                                      )}
                                    </div>
                                  </CommonLink>
                                </li>
                              );
                            },
                          )
                        ) : (
                          <>
                            <div>
                              {searchWord}に一致する情報は見つかりませんでした。
                            </div>
                            <div>
                              キーワードが正しく入力されているかご確認ください。
                            </div>
                            <div className="p-12 border border-primary-1 rounded my-32">
                              <div className="font-bold">
                                人気の検索キーワード
                              </div>
                              <div className="my-8 text-sm">
                                {recommendKeywords.map((keyword) => {
                                  return (
                                    <CommonLink
                                      href={`/search?q=${keyword}`}
                                      className="text-primary-1 hover:cursor-pointer hover:underline mr-8 whitespace-nowrap pc:text-base"
                                    >
                                      {keyword}
                                    </CommonLink>
                                  );
                                })}
                              </div>
                            </div>
                          </>
                        )}
                      </ul>
                    </div>
                    {searchResult.length > 0 && nextPage > 0 && (
                      <button
                        type="button"
                        onClick={() => {
                          void getSearchKeywordNextPage(
                            searchWord,
                            type,
                            nextPage,
                          );
                        }}
                        className="my-8 w-full bg-black-3 py-16 text-center text-white-1"
                      >
                        もっと見る
                      </button>
                    )}
                  </>
                )}
              </Box>
              {/* 右カラム */}
              {isPC && (
                <Box className="w-[320px]">
                  <Box className="sticky top-[150px] flex flex-col gap-y-8">
                    <Box className="sticky flex flex-col gap-y-8">
                      <GoogleAdBanner
                        googleAdBannerName={GoogleAdBannerName.B_RECTANGLE}
                      />
                      {!isValid && <SignUp />}
                      <GoogleAdBanner
                        googleAdBannerName={
                          GoogleAdBannerName.B_RECTANGLE_BOTTOM
                        }
                      />
                    </Box>
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </main>
      {!isWebView && <GeneralFooter />}
    </>
  );
};
